import { useCallback, useEffect, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import {
	type EditionAwarenessContextType,
	useEditionAwareness,
} from '../services/edition-awareness-context';
import { useExperimentContext } from '../services/experiment-context';

export const ANALYTICS_CHANNEL: FabricChannel.growth = FabricChannel.growth;

type EditionAwarenessAnalyticsContextType = Omit<
	EditionAwarenessContextType,
	'edition' | 'invoiceableStatus' | 'isInCollection'
> & {
	currentProductEdition: EditionAwarenessContextType['edition']; // currentProductEdition is part of Common Instrumentation Event Attribute Spec
	isEnforceStorageLimitsEnabled: boolean;
	isInCollection: boolean | null; // Keep the analytics contract stable while the context uses string values
	isInvoiceable: boolean | null; // We use the invoiceableStatus for checking the value, but send isInvoiceable as part of analytics
};

function getAnalyticsBoolean<TValue>({
	value,
	whenTrue,
	whenFalse,
}: {
	value: TValue;
	whenFalse: TValue;
	whenTrue: TValue;
}): boolean | null {
	if (value === whenTrue) {
		return true;
	}

	if (value === whenFalse) {
		return false;
	}

	return null;
}

export const getAttributesFromContexts = (context: unknown): Record<string, any> => {
	if (!context || !Array.isArray(context)) {
		return {};
	}

	return context.reduce((acc, x) => {
		if (x.attributes) {
			return {
				...acc,
				...x.attributes,
			};
		}

		return {
			...acc,
			...(x ?? {}),
		};
	}, {});
};

export const defaultAttributes = {
	packageName: '@atlassian/edition-awareness',
	source: 'editionAwareness',
	featureId: 'edition-awareness',
	growthPillar: 'BUY',
};

export function useAnalytics(): {
	sendAnalyticsEvent: (payload: AnalyticsEventPayload | UIAnalyticsEvent) => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const editionAwarenessContext = useEditionAwareness();
	const experimentContext = useExperimentContext();

	const sendAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload | UIAnalyticsEvent): void => {
			const editionAwarenessContextAttributes: EditionAwarenessAnalyticsContextType = {
				// Required properties
				cloudId: editionAwarenessContext.cloudId,
				product: editionAwarenessContext.product,
				currentProductEdition: editionAwarenessContext.edition,

				// Payment properties
				isInvoiceable: getAnalyticsBoolean({
					value: editionAwarenessContext.invoiceableStatus,
					whenTrue: 'INVOICEABLE',
					whenFalse: 'NOT_INVOICEABLE',
				}),
				hasPaymentDetailsEntered: editionAwarenessContext.hasPaymentDetailsEntered,
				canAddPaymentDetails: editionAwarenessContext.canAddPaymentDetails,
				addPaymentDetailsExperienceUrl: editionAwarenessContext.addPaymentDetailsExperienceUrl,

				// Trial properties
				trialDaysLeft: editionAwarenessContext.trialDaysLeft,
				trialLengthDays: editionAwarenessContext.trialLengthDays,
				trialEndDate: editionAwarenessContext.trialEndDate,
				isInTrial: editionAwarenessContext.isInTrial,
				isInPredunning: editionAwarenessContext.isInPredunning,
				isInReverseTrial: editionAwarenessContext.isInReverseTrial,
				lastKnownTrial: editionAwarenessContext?.lastKnownTrial,
				offeringKey: editionAwarenessContext?.offeringKey,

				// Upgrade properties
				canUpgrade: editionAwarenessContext.canUpgrade,
				canDirectBuyUpgrade: editionAwarenessContext.canDirectBuyUpgrade,
				changeOfferingToStandardExperienceUrl:
					editionAwarenessContext.changeOfferingToStandardExperienceUrl,
				directBuyChangeOfferingToStandardExperienceUrl:
					editionAwarenessContext.directBuyChangeOfferingToStandardExperienceUrl,

				// Entitlement properties
				entitlementId: editionAwarenessContext.entitlementId,
				isInCollection: getAnalyticsBoolean({
					value: editionAwarenessContext.isInCollection,
					whenTrue: 'IN_COLLECTION',
					whenFalse: 'NOT_IN_COLLECTION',
				}),
				collectionType: editionAwarenessContext.collectionType,
				isSinglePaidApp: editionAwarenessContext.isSinglePaidApp,
				canBundleCollection: editionAwarenessContext.canBundleCollection,

				// User permissions properties
				userRoles: editionAwarenessContext.userRoles,

				// Site properties
				licensedUsers: editionAwarenessContext.licensedUsers,
				storageUsageInGB: editionAwarenessContext.storageUsageInGB,
				storageLimitInGB: editionAwarenessContext.storageLimitInGB,
				storageIsUnlimited: editionAwarenessContext.storageIsUnlimited,
				isEnterpriseCustomer: editionAwarenessContext.isEnterpriseCustomer,

				// Site traits
				automationUsage: editionAwarenessContext.automationUsage,
				lastAutomationFreeLimitBreachDate:
					editionAwarenessContext.lastAutomationFreeLimitBreachDate,
				automationFreeLimitStatus: editionAwarenessContext.automationFreeLimitStatus,
				isEligibleForJsmStandardToPremium:
					editionAwarenessContext.isEligibleForJsmStandardToPremium,
				isEligibleForJswStandardToPremium:
					editionAwarenessContext.isEligibleForJswStandardToPremium,
				isEligibleForJpdStandardToPremium:
					editionAwarenessContext.isEligibleForJpdStandardToPremium,
				isEligibleForConfluenceStandardToPremium:
					editionAwarenessContext.isEligibleForConfluenceStandardToPremium,
				isEligibleForJswPremiumToTwc: editionAwarenessContext.isEligibleForJswPremiumToTwc,
				isEligibleForConfluencePremiumToTwc:
					editionAwarenessContext.isEligibleForConfluencePremiumToTwc,

				// Capabilities
				shouldAutoOpen: editionAwarenessContext.shouldAutoOpen,

				isEnforceStorageLimitsEnabled:
					editionAwarenessContext.edition === 'free' &&
					(editionAwarenessContext.product === 'jira-service-management' ||
						editionAwarenessContext.product === 'jira-customer-service') &&
					FeatureGates.getExperimentValue<boolean>(
						'jsmgrowth-267_enforce_storage_limits_servco_free',
						'isEnabled',
						false,
					),

				// For twcg-1563 experiment, temporary attribute for the introduction of full JWM support
				originalProjectTypeWasCore: editionAwarenessContext.originalProjectTypeWasCore,
			};

			if (payload instanceof UIAnalyticsEvent) {
				const contextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						attributes: {
							...contextAttributes,
							...defaultAttributes,
							...payload.payload.attributes,
							...editionAwarenessContextAttributes,
							experiments: experimentContext.experiments(),
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					attributes: {
						...defaultAttributes,
						...payload.attributes,
						...editionAwarenessContextAttributes,
						experiments: experimentContext.experiments(),
					},
				});

				const contextAttributes = getAttributesFromContexts(event.context);

				event
					.update({
						attributes: {
							...contextAttributes,
							...event.payload.attributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent, editionAwarenessContext, experimentContext],
	);

	return {
		sendAnalyticsEvent,
	};
}

/**
 * Put at the end of your render tree, will fire a screen event once when first rendered with the `name`
 */
export function ScreenEvent({
	name,
	attributes,
}: {
	attributes?: Record<string, unknown>;
	name: string;
}) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}
		sendAnalyticsEvent({
			eventType: 'screen',
			name,
			attributes,
		});
		hasSent.current = true;
	}, [sendAnalyticsEvent, name, attributes]);

	return null;
}
